import React from 'react';
import { Link } from 'react-router-dom';
import { ABOUT_STATS, FOUNDER_TRACK, TEAM } from '@/data/content';
import { Button } from '@/components/ui/button';
import { ArrowRight, UserRound, Linkedin } from 'lucide-react';

function TeamCard({ member }) {
  const [imgFailed, setImgFailed] = React.useState(!member.img);
  return (
    <div className="group rounded-2xl border border-white/10 bg-white/[0.02] p-5 hover:border-cyan-400/30 hover:bg-white/[0.04] transition-all">
      <div className="relative aspect-[4/5] rounded-xl overflow-hidden bg-gradient-to-br from-slate-800 via-slate-900 to-black mb-4">
        {!imgFailed && (
          <img
            src={member.img}
            alt={member.name}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover object-top grayscale group-hover:grayscale-0 transition-all"
            onError={() => setImgFailed(true)}
          />
        )}
        {imgFailed && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/15 via-sky-500/10 to-transparent" />
            <div className="relative w-24 h-24 rounded-full bg-white/5 border border-cyan-400/30 flex items-center justify-center backdrop-blur-sm">
              <UserRound className="w-12 h-12 text-cyan-300/70" strokeWidth={1.25} />
            </div>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#07090c] via-transparent to-transparent pointer-events-none" />

        {member.linkedin && (
          <a
            href={member.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${member.name} on LinkedIn`}
            className="absolute bottom-3 right-3 z-10 inline-flex items-center justify-center w-9 h-9 rounded-md bg-[#0a66c2] hover:bg-[#0d7fee] text-white shadow-lg shadow-black/40 border border-white/10 transition-colors"
          >
            <Linkedin className="w-4 h-4" strokeWidth={2.25} />
          </a>
        )}
      </div>
      <div className="font-display text-lg font-semibold text-slate-100">{member.name}</div>
      <div className="text-[10px] font-mono uppercase tracking-widest text-cyan-300 mt-1">{member.role}</div>
      <p className="text-xs text-slate-400 mt-3 leading-relaxed">{member.bio}</p>
    </div>
  );
}

export default function AboutPage() {
  return (
    <div>
      {/* Hero */}
      <section className="relative py-24 md:py-32 border-b border-white/5 overflow-hidden">
        <div className="absolute inset-0 viso-grid opacity-40" />
        <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-cyan-400/10 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="text-xs font-mono uppercase tracking-[0.35em] text-cyan-300 mb-4">About us</div>
          <h1 className="font-display text-4xl md:text-6xl font-bold max-w-4xl leading-[1.05]">
            <span className="text-gradient">Builders of AI for the physical world.</span>
          </h1>
          <p className="text-slate-400 mt-6 max-w-3xl text-base md:text-lg leading-relaxed">
            We operate where complexity is highest — yards, gates, warehouses and terminals — turning messy, real-world visual operations into measurable intelligence, from damage inspection to gate automation and cargo counting.
          </p>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
            {ABOUT_STATS.map((s) => (
              <div key={s.l} className="rounded-xl border border-white/10 bg-white/[0.02] p-5">
                <div className="font-display text-3xl md:text-4xl font-bold text-cyan-300">{s.v}</div>
                <div className="text-xs text-slate-400 mt-1">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Founder track record */}
      <section className="py-16 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-xs font-mono uppercase tracking-[0.35em] text-cyan-300 mb-3">Founder track record</div>
          <h2 className="font-display text-2xl md:text-4xl font-bold">A decade of founder-built brands.</h2>
          <div className="mt-8 flex flex-wrap gap-2">
            {FOUNDER_TRACK.map((b) => (
              <span
                key={b}
                className={`px-4 py-2 rounded-full text-sm border ${b === 'Visotonics' ? 'bg-cyan-400/10 border-cyan-400/40 text-cyan-200' : 'bg-white/[0.03] border-white/10 text-slate-300'}`}
              >
                {b}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-20 bg-[#0a0d12]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-xs font-mono uppercase tracking-[0.35em] text-cyan-300 mb-3">The leadership team</div>
          <h2 className="font-display text-3xl md:text-5xl font-bold max-w-3xl leading-tight">
            A decade of computer vision &amp; AI experience.
          </h2>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-5">
            {TEAM.map((m) => (
              <TeamCard key={m.id} member={m} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="font-display text-2xl md:text-3xl font-bold">
              Come build the vision layer of physical operations.
            </div>
            <p className="text-slate-400 mt-2 max-w-xl">
              We’re hiring engineers who like the world of atoms as much as bits.
            </p>
          </div>
          <Link to="/contact">
            <Button className="bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-medium">
              Talk to us <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
